import { prisma, EntityModel, Prisma } from "client";
import { enrichSessionsWithSpeakers } from "../session-v2";
import {
  participantGroupColorSelect,
  pickPartyGroupColor,
} from "../participantGroup/party-group-color.pure";

export const toggleBookmark = async ({
  userId,
  eventId,
  entityId,
  entityModel,
}: {
  userId: string;
  eventId: string;
  entityId: string;
  entityModel: EntityModel;
}) => {
  try {
    const existing = await prisma.bookmark.findFirst({
      where: {
        userId,
        eventId,
        entityId,
        entityModel,
      },
    });

    if (existing) {
      await prisma.bookmark.delete({
        where: {
          id: existing.id,
        },
      });
      return { status: "unbookmarked" };
    } else {
      await prisma.bookmark.create({
        data: {
          userId,
          eventId,
          entityId,
          entityModel,
        },
      });
      return { status: "bookmarked" };
    }
  } catch (error) {
    console.error("Error toggling bookmark:", error);
    throw new Error("Failed to toggle bookmark");
  }
};

function translationInclude(lang: string) {
  return lang
    ? {
        where: {
          language: { code: lang },
        },
      }
    : true;
}

function includeForType(
  type: EntityModel,
  userId: string,
  eventId: string,
  lang: string
): Prisma.BookmarkInclude {
  switch (type) {
    case EntityModel.PROFILE:
      return {
        bookmarkedUser: {
          include: {
            tags: {
              where: {
                eventId,
              },
            },
            profiles: {
              where: {
                eventId,
              },
              orderBy: {
                createdAt: "desc",
              },
              take: 1,
              include: {
                participantGroups: {
                  select: {
                    type: true,
                    color: true,
                    translations: {
                      where: {
                        language: { code: lang },
                      },
                    },
                  },
                },
              },
            },
            bookmarks: {
              where: { userId },
            },
            connectionRequestsReceived: {
              where: {
                OR: [{ senderId: userId }, { receiverId: userId }],
              },
            },
            connectionRequestsSent: {
              where: {
                OR: [{ senderId: userId }, { receiverId: userId }],
              },
            },
            networking: true,
          },
        },
        // Lean company cards for PROFILE bookmarks stored as company id.
        exhibitor: {
          include: {
            translations: translationInclude(lang),
            participantGroup: participantGroupColorSelect,
          },
        },
        sponsor: {
          include: {
            translations: translationInclude(lang),
            participantGroup: participantGroupColorSelect,
          },
        },
      };
    case EntityModel.SESSION:
      return {
        session: {
          include: {
            translations: translationInclude(lang),
          },
        },
      };
    case EntityModel.PRODUCT:
      return {
        product: {
          include: {
            translations: translationInclude(lang),
          },
        },
      };
    case EntityModel.DOCUMENT:
      return {
        document: {
          include: {
            translations: translationInclude(lang),
          },
        },
      };
    case EntityModel.VIDEO:
      return {
        video: {
          include: {
            translations: translationInclude(lang),
          },
        },
      };
    default:
      return {};
  }
}

export const getBookmarksByUser = async (
  userId: string,
  eventId: string,
  lang: string = "en",
  page: number = 1,
  limit: number = 10,
  type: EntityModel
) => {
  const bookmarkWhere: Prisma.BookmarkWhereInput = {
    userId,
    entityModel: type,
    ...(eventId && { eventId }),
    ...(type === EntityModel.PROFILE && {
      OR: [
        {
          bookmarkedUser: {
            profiles: {
              some: {
                eventId,
                active: true,
              },
            },
          },
        },
        // Company PROFILE bookmarks (entityId = exhibitor/sponsor id for this event).
        { exhibitor: { is: { eventId } } },
        { sponsor: { is: { eventId } } },
      ],
    }),
  };

  const [bookmarks, totalCount] = await Promise.all([
    prisma.bookmark.findMany({
      where: bookmarkWhere,
      include: includeForType(type, userId, eventId, lang),
      skip: (page - 1) * limit,
      take: limit,
      orderBy: {
        createdAt: "desc",
      },
    }),
    prisma.bookmark.count({
      where: bookmarkWhere,
    }),
  ]);

  // Dynamic include — narrow for nested relation access per type
  type BookmarkListItem = (typeof bookmarks)[number] & {
    bookmarkedUser?: {
      bookmarks?: unknown[];
      connectionRequestsReceived?: { status: string }[];
      connectionRequestsSent?: { status: string }[];
      profiles?: Array<{
        participantGroups?: Array<{ color?: string | null }>;
      }>;
    } | null;
    exhibitor?: {
      participantGroup?: { color?: string | null } | null;
    } | null;
    sponsor?: {
      participantGroup?: { color?: string | null } | null;
    } | null;
    session?: {
      id: string;
      speakerUserIds?: string[] | null;
    } | null;
  };

  let transformedData = (bookmarks as BookmarkListItem[]).map((bookmark) => {
    const isBookmarked = !!(bookmark.bookmarkedUser?.bookmarks?.length);

    const connectionRequests =
      bookmark.bookmarkedUser?.connectionRequestsReceived?.[0]?.status ??
      bookmark.bookmarkedUser?.connectionRequestsSent?.[0]?.status ??
      null;

    return {
      ...bookmark,
      bookmarkedUser: bookmark.bookmarkedUser
        ? {
            ...bookmark.bookmarkedUser,
            connectionRequests,
            isBookmarked: isBookmarked || type === EntityModel.PROFILE,
            color: pickPartyGroupColor(bookmark.bookmarkedUser),
          }
        : null,
      exhibitor: bookmark.exhibitor
        ? {
            ...bookmark.exhibitor,
            isBookmarked: true,
            color: pickPartyGroupColor(bookmark.exhibitor),
          }
        : null,
      sponsor: bookmark.sponsor
        ? {
            ...bookmark.sponsor,
            isBookmarked: true,
            color: pickPartyGroupColor(bookmark.sponsor),
          }
        : null,
      session: bookmark.session
        ? {
            ...bookmark.session,
            isBookmarked: true,
          }
        : null,
    };
  });

  if (type === EntityModel.SESSION) {
    const sessions = transformedData
      .map((bookmark) => bookmark.session)
      .filter((session): session is NonNullable<typeof session> => !!session);

    if (sessions.length) {
      const enriched = await enrichSessionsWithSpeakers(
        sessions.map((session) => ({
          ...session,
          speakerUserIds: session.speakerUserIds ?? [],
          eventId,
        }))
      );
      const enrichedMap = new Map(
        enriched.map((session) => [session.id, session])
      );

      transformedData = transformedData.map((bookmark) => ({
        ...bookmark,
        session: bookmark.session
          ? (enrichedMap.get(bookmark.session.id) ?? bookmark.session)
          : null,
      }));
    }
  }

  const totalPages = Math.ceil(totalCount / limit);
  return {
    data: transformedData,
    pagination: {
      total: totalCount,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
      currentPage: page,
    },
  };
};

export const getBookmarkedEntityIds = async (
  userId: string,
  eventId: string,
  entityModel: EntityModel,
  entityIds: string[]
) => {
  if (!entityIds.length) return new Set<string>();

  const bookmarks = await prisma.bookmark.findMany({
    where: {
      userId,
      eventId,
      entityModel,
      entityId: { in: entityIds },
    },
    select: { entityId: true },
  });

  return new Set(bookmarks.map((bookmark) => bookmark.entityId));
};
